import { useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { useAuth, useAuthStoreHydrated } from '@/stores/use-auth';
import { isServerSessionValidated } from '@/lib/auth-session-state';
import { restoreSession } from '@/lib/session';

function isSessionReady(): boolean {
  return isServerSessionValidated() && useAuth.getState().isAuthenticated;
}

export interface AuthReadiness {
  /** True once the persisted store is hydrated and the server session was restored or refused. */
  isReady: boolean;
  hasResolvedAuth: boolean;
  /** Set once this mount saw a live session (lets `ProtectedRoute` tell "lost" from "never had"). */
  hadLiveSessionRef: MutableRefObject<boolean>;
}

/**
 * Restores the server session once the auth store is hydrated, and says when the answer is known.
 *
 * A full-page redirect back into the app (OAuth, Microsoft consent) starts with no validated
 * session: until `restoreSession()` settles, `isAuthenticated` is not an answer yet. Protected
 * routes use this through `ProtectedRoute`; a public page that behaves differently for signed-in
 * users must call it itself, since nothing else restores the session there.
 */
export function useAuthReady(): AuthReadiness {
  const { isLoading, setLoading, logout } = useAuth();
  const authStoreHydrated = useAuthStoreHydrated();
  const [isCheckingAuth, setIsCheckingAuth] = useState(() => !isSessionReady());
  const [hasResolvedAuth, setHasResolvedAuth] = useState(() => isSessionReady());
  const hadLiveSessionRef = useRef(isSessionReady());

  useLayoutEffect(() => {
    if (!authStoreHydrated) {
      return;
    }

    let cancelled = false;

    async function validate() {
      try {
        if (isSessionReady()) {
          hadLiveSessionRef.current = true;
          return;
        }

        setIsCheckingAuth(true);
        setLoading(true);

        const ok = await restoreSession().catch(() => false);

        if (cancelled) {
          return;
        }

        if (ok) {
          hadLiveSessionRef.current = true;
        } else if (useAuth.getState().isAuthenticated) {
          logout();
        }
      } finally {
        setLoading(false);
        setIsCheckingAuth(false);
        if (!cancelled) {
          setHasResolvedAuth(true);
        }
      }
    }

    void validate();

    return () => {
      cancelled = true;
    };
  }, [authStoreHydrated, setLoading, logout]);

  return {
    isReady: authStoreHydrated && !isLoading && !isCheckingAuth && hasResolvedAuth,
    hasResolvedAuth,
    hadLiveSessionRef,
  };
}
